import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Loader2, CheckCircle2, Banknote, CreditCard, Truck, Store, Calendar, MapPin, Phone, Clock, ExternalLink } from "lucide-react";
import { Header } from "../components/Header";
import { CartSheet } from "../components/CartSheet";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { useSession } from "../context/SessionContext";
import { useCart } from "../context/CartContext";
import { clientApi, formatCOP } from "../lib/api";
import { formatUnit } from "../lib/units";
import { toast } from "sonner";

export default function CheckoutReview() {
  const { customer } = useSession();
  const { items, total, clear } = useCart();
  const [obs, setObs] = useState("");
  const [tipoPagoUser, setTipoPago] = useState("0");           // "0" Contado · "1" Crédito
  const [tipoEntrega, setTipoEntrega] = useState("domicilio"); // "domicilio" · "recoger"
  const [deliveryInfo, setDeliveryInfo] = useState(null); // { estimate, bodega }
  const [cartera, setCartera] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    clientApi.get("/portal/delivery-info")
      .then(({ data }) => setDeliveryInfo(data))
      .catch(() => {});
    clientApi.get("/portal/cartera")
      .then(({ data }) => setCartera(data || null))
      .catch(() => {});
  }, []);

  // Effective tipo_pago: forces "contado" if cartera blocks credit, regardless of user click
  const creditBlocked = cartera && !cartera.permite_credito;
  const tipoPago = creditBlocked && tipoPagoUser === "1" ? "0" : tipoPagoUser;

  if (!customer) return null;

  const confirm = async () => {
    if (items.length === 0) { toast.error("Tu carrito está vacío"); return; }
    setSubmitting(true);
    try {
      const payload = {
        lineas: items.map((i) => ({ codigo: i.codigo, cantidad: i.cantidad })),
        observaciones: obs || null,
        tipo_pago: tipoPago,
        tipo_entrega: tipoEntrega,
      };
      const { data } = await clientApi.post("/portal/orders", payload);
      clear();
      navigate(`/pedido-exitoso/${data.order_number}`, { state: { order: data } });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No pudimos crear el pedido");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-cm-cream" data-testid="checkout-page">
      <Header />
      <CartSheet />

      <main className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/catalogo" className="inline-flex items-center text-sm text-cm-ink-soft hover:text-cm-red mb-4" data-testid="back-to-catalog">
          <ArrowLeft className="h-4 w-4 mr-1" /> Seguir comprando
        </Link>

        <div className="mb-6">
          <p className="text-xs uppercase tracking-widest text-cm-ink-soft font-medium">Revisión</p>
          <h1 className="font-serif text-3xl sm:text-4xl mt-1 tracking-tight">Confirma tu pedido</h1>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Items + customer */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="p-6 bg-white border-cm-border shadow-none">
              <h2 className="font-serif text-xl mb-4 flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-cm-red" /> Datos del cliente
              </h2>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm" data-testid="customer-data-block">
                <div><span className="text-cm-ink-soft">Razón social: </span><span className="font-medium">{customer.razon_social}</span></div>
                <div><span className="text-cm-ink-soft">NIT: </span><span className="font-mono">{customer.nit}</span></div>
                {customer.almacen && <div><span className="text-cm-ink-soft">Almacén: </span>{customer.almacen}</div>}
                {customer.nombre_representante && <div><span className="text-cm-ink-soft">Representante: </span>{customer.nombre_representante}</div>}
                <div><span className="text-cm-ink-soft">Dirección: </span>{customer.direccion}</div>
                <div><span className="text-cm-ink-soft">Teléfono: </span><span className="font-mono">{customer.telefono}</span></div>
                <div><span className="text-cm-ink-soft">Ciudad: </span>{customer.ciudad}</div>
                <div><span className="text-cm-ink-soft">Categoría: </span><span className="font-semibold text-cm-red">{customer.lista_precios_nombre}</span></div>
                {customer.plazo_dias > 0 && <div><span className="text-cm-ink-soft">Plazo crédito: </span><span className="font-mono">{customer.plazo_dias} días</span></div>}
                {customer.cupo > 0 && <div><span className="text-cm-ink-soft">Cupo: </span><span className="font-mono">{formatCOP(customer.cupo)}</span></div>}
              </div>
            </Card>

            <Card className="bg-white border-cm-border shadow-none overflow-hidden">
              <div className="px-6 py-4 border-b border-cm-border">
                <h2 className="font-serif text-xl">Productos ({items.length})</h2>
              </div>
              {items.length === 0 ? (
                <div className="p-8 text-center text-cm-ink-soft">
                  <p>Tu carrito está vacío. <Link to="/catalogo" className="text-cm-red font-medium underline">Volver al catálogo</Link></p>
                </div>
              ) : (
                <div className="divide-y divide-cm-border">
                  {items.map((it) => (
                    <div key={it.codigo} className="flex items-center gap-4 px-6 py-3" data-testid={`review-line-${it.codigo}`}>
                      {it.imagen && <img src={it.imagen} alt="" className="h-12 w-12 rounded object-cover border border-cm-border" />}
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-mono text-cm-ink-soft">{it.codigo}</p>
                        <p className="text-sm font-medium line-clamp-1">{it.descripcion}</p>
                        <p className="text-xs text-cm-ink-soft">{formatUnit(it.unidad)}</p>
                      </div>
                      <div className="text-right text-sm">
                        <p className="font-mono">{it.cantidad} × {formatCOP(it.precio)}</p>
                        <p className="font-mono font-semibold">{formatCOP((it.precio || 0) * it.cantidad)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card className="p-6 bg-white border-cm-border shadow-none">
              <h2 className="font-serif text-xl mb-4">Forma de pago</h2>
              <div className="grid grid-cols-2 gap-3" data-testid="tipo-pago-selector">
                <button
                  type="button"
                  onClick={() => setTipoPago("0")}
                  className={`flex flex-col items-start text-left p-4 rounded-md border-2 transition-all ${
                    tipoPago === "0"
                      ? "border-cm-red bg-cm-red/5"
                      : "border-cm-border hover:border-cm-ink/30"
                  }`}
                  data-testid="tipo-pago-contado"
                >
                  <Banknote className={`h-5 w-5 mb-2 ${tipoPago === "0" ? "text-cm-red" : "text-cm-ink-soft"}`} />
                  <span className="font-medium text-sm">Contado / Efectivo</span>
                  <span className="text-[11px] text-cm-ink-soft mt-1">Pago al recibir el pedido</span>
                </button>
                <button
                  type="button"
                  onClick={() => setTipoPago("1")}
                  disabled={cartera ? !cartera.permite_credito : false}
                  className={`flex flex-col items-start text-left p-4 rounded-md border-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                    tipoPago === "1"
                      ? "border-cm-red bg-cm-red/5"
                      : "border-cm-border hover:border-cm-ink/30"
                  }`}
                  data-testid="tipo-pago-credito"
                >
                  <CreditCard className={`h-5 w-5 mb-2 ${tipoPago === "1" ? "text-cm-red" : "text-cm-ink-soft"}`} />
                  <span className="font-medium text-sm">Crédito</span>
                  <span className="text-[11px] text-cm-ink-soft mt-1">
                    {cartera && !cartera.permite_credito
                      ? (cartera.status === "rojo"
                          ? `No disponible — ${cartera.n_vencidas} factura${cartera.n_vencidas === 1 ? "" : "s"} vencida${cartera.n_vencidas === 1 ? "" : "s"}`
                          : "No disponible — sin cupo de crédito")
                      : customer.plazo_dias > 0 ? `Plazo ${customer.plazo_dias} días` : "Según condiciones"}
                  </span>
                </button>
              </div>
              {cartera?.nota_credito && tipoPago === "1" && (
                <p className="mt-3 text-xs text-cm-ink-soft bg-cm-yellow/20 border border-cm-yellow/40 rounded p-2.5" data-testid="checkout-nota-credito">
                  ℹ {cartera.nota_credito}
                </p>
              )}
            </Card>

            <Card className="p-6 bg-white border-cm-border shadow-none">
              <h2 className="font-serif text-xl mb-4 flex items-center gap-2">
                <Truck className="h-5 w-5 text-cm-red" /> Tipo de entrega
              </h2>
              <div className="grid grid-cols-2 gap-3" data-testid="tipo-entrega-selector">
                <button
                  type="button"
                  onClick={() => setTipoEntrega("domicilio")}
                  className={`flex flex-col items-start text-left p-4 rounded-md border-2 transition-all ${
                    tipoEntrega === "domicilio"
                      ? "border-cm-red bg-cm-red/5"
                      : "border-cm-border hover:border-cm-ink/30"
                  }`}
                  data-testid="tipo-entrega-domicilio"
                >
                  <Truck className={`h-5 w-5 mb-2 ${tipoEntrega === "domicilio" ? "text-cm-red" : "text-cm-ink-soft"}`} />
                  <span className="font-medium text-sm">Entrega a domicilio</span>
                  <span className="text-[11px] text-cm-ink-soft mt-1 line-clamp-2">
                    {customer.direccion ? customer.direccion : "A la dirección registrada"}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setTipoEntrega("recoger")}
                  className={`flex flex-col items-start text-left p-4 rounded-md border-2 transition-all ${
                    tipoEntrega === "recoger"
                      ? "border-cm-red bg-cm-red/5"
                      : "border-cm-border hover:border-cm-ink/30"
                  }`}
                  data-testid="tipo-entrega-recoger"
                >
                  <Store className={`h-5 w-5 mb-2 ${tipoEntrega === "recoger" ? "text-cm-red" : "text-cm-ink-soft"}`} />
                  <span className="font-medium text-sm">Recoger en bodega</span>
                  <span className="text-[11px] text-cm-ink-soft mt-1">CM Calidad y Servicio · Cartagena</span>
                </button>
              </div>
            </Card>

            <Card className="p-6 bg-white border-cm-border shadow-none">
              <h2 className="font-serif text-xl mb-4 flex items-center gap-2">
                <Calendar className="h-5 w-5 text-cm-red" />
                {tipoEntrega === "recoger" ? "Disponible para recoger" : "Tiempo estimado de entrega"}
              </h2>
              {deliveryInfo ? (
                <div className="bg-cm-yellow/15 border border-cm-yellow/60 rounded-md px-4 py-3 flex items-start gap-3" data-testid="delivery-estimate">
                  <div className="flex-shrink-0 mt-0.5">
                    {tipoEntrega === "recoger" ? (
                      <Store className="h-5 w-5 text-cm-red" />
                    ) : (
                      <Truck className="h-5 w-5 text-cm-red" />
                    )}
                  </div>
                  <div className="text-sm leading-relaxed">
                    <p className="font-medium">
                      {tipoEntrega === "recoger" ? "Listo entre" : "Llega entre"}{" "}
                      <span className="text-cm-red">
                        {deliveryInfo.estimate?.min?.label_dia} {deliveryInfo.estimate?.min?.label_corto}
                      </span>{" "}
                      y{" "}
                      <span className="text-cm-red">
                        {deliveryInfo.estimate?.max?.label_dia} {deliveryInfo.estimate?.max?.label_corto}
                      </span>
                    </p>
                    <p className="text-[11px] text-cm-ink-soft mt-1">
                      {tipoEntrega === "recoger"
                        ? "Te avisaremos por WhatsApp en cuanto esté listo para recoger."
                        : "Sin costo adicional. No realizamos entregas los domingos ni festivos."}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-cm-ink-soft">Calculando tiempo estimado…</p>
              )}

              {tipoEntrega === "recoger" && deliveryInfo?.bodega && (
                <div className="mt-4 border-t border-cm-border pt-4" data-testid="bodega-info">
                  <p className="text-[10px] uppercase tracking-widest text-cm-ink-soft font-medium mb-2">
                    Punto de recogida
                  </p>
                  <p className="font-medium text-cm-ink mb-1.5">{deliveryInfo.bodega.nombre}</p>
                  {deliveryInfo.bodega.direccion && (
                    <p className="text-sm flex items-start gap-2 text-cm-ink-soft">
                      <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0 text-cm-red" />
                      <span>{deliveryInfo.bodega.direccion}</span>
                    </p>
                  )}
                  {deliveryInfo.bodega.horario && (
                    <p className="text-sm flex items-start gap-2 text-cm-ink-soft mt-1.5">
                      <Clock className="h-4 w-4 mt-0.5 flex-shrink-0 text-cm-red" />
                      <span>{deliveryInfo.bodega.horario}</span>
                    </p>
                  )}
                  {deliveryInfo.bodega.telefono && (
                    <p className="text-sm flex items-start gap-2 text-cm-ink-soft mt-1.5">
                      <Phone className="h-4 w-4 mt-0.5 flex-shrink-0 text-cm-red" />
                      <a href={`tel:${deliveryInfo.bodega.telefono.replace(/\s/g, "")}`} className="hover:text-cm-red hover:underline">
                        {deliveryInfo.bodega.telefono}
                      </a>
                    </p>
                  )}
                  {deliveryInfo.bodega.maps_url && (
                    <a
                      href={deliveryInfo.bodega.maps_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 mt-3 px-3 py-2 rounded-md bg-cm-ink text-cm-cream hover:bg-cm-ink/90 transition-colors text-sm font-medium"
                      data-testid="bodega-maps-link"
                    >
                      <MapPin className="h-3.5 w-3.5" />
                      Cómo llegar
                      <ExternalLink className="h-3 w-3 opacity-70" />
                    </a>
                  )}
                </div>
              )}
            </Card>

            <Card className="p-6 bg-white border-cm-border shadow-none">
              <label className="block text-sm font-medium mb-2">Observaciones (opcional)</label>
              <Textarea
                placeholder="Indica preferencia de entrega, contacto en el local, etc."
                value={obs}
                onChange={(e) => setObs(e.target.value)}
                className="border-cm-border focus-visible:ring-cm-red focus-visible:ring-offset-0"
                rows={3}
                data-testid="observations-input"
              />
            </Card>
          </div>

          {/* Summary */}
          <div className="lg:col-span-1">
            <Card className="sticky top-20 p-6 bg-cm-ink text-cm-cream border-0 shadow-none">
              <h2 className="font-serif text-xl mb-4">Resumen</h2>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-cm-cream/70">Productos</span>
                  <span className="font-mono">{items.length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-cm-cream/70">Forma de pago</span>
                  <span className="font-medium">{tipoPago === "0" ? "Contado" : "Crédito"}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-cm-cream/70">Entrega</span>
                  <span className="font-medium">{tipoEntrega === "recoger" ? "Recoger en bodega" : "A domicilio"}</span>
                </div>
                {deliveryInfo?.estimate?.max && (
                  <div className="flex justify-between text-sm">
                    <span className="text-cm-cream/70">Estimado</span>
                    <span className="font-medium text-right text-[11px] leading-tight">
                      {deliveryInfo.estimate.min.label_dia} – {deliveryInfo.estimate.max.label_dia}<br/>
                      <span className="text-cm-cream/60 text-[10px]">
                        {deliveryInfo.estimate.min.label_corto} – {deliveryInfo.estimate.max.label_corto}
                      </span>
                    </span>
                  </div>
                )}
                <div className="border-t border-cm-cream/20 pt-3 flex justify-between items-baseline">
                  <span className="font-serif text-lg">Total a pagar</span>
                  <span className="font-mono text-2xl font-bold text-cm-yellow" data-testid="review-total">{formatCOP(total)}</span>
                </div>
                <p className="text-[10px] text-cm-cream/60">Precios incluyen IVA</p>
              </div>
              <Button
                onClick={confirm}
                disabled={submitting || items.length === 0 || (cartera && !cartera.permite_pedido)}
                className="w-full h-12 mt-6 bg-cm-yellow text-cm-ink hover:bg-cm-yellow-dark font-bold disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="confirm-order-btn"
              >
                {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Enviando...</> : "Confirmar pedido"}
              </Button>
              {cartera && !cartera.permite_pedido && (
                <p className="text-[12px] text-cm-yellow mt-3 leading-relaxed" data-testid="pedido-blocked-msg">
                  ⚠ {cartera.mensaje || "Tu cuenta tiene una restricción de facturación. Contacta al equipo comercial."}
                </p>
              )}
              <p className="text-[11px] text-cm-cream/60 mt-3 leading-relaxed">
                Tu pedido se procesa el siguiente día hábil (lun a vie, excluyendo festivos).
                Recibirás confirmación con número de seguimiento.
              </p>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
